import { MenuItem, PopoverPosition, Position } from '@blueprintjs/core';
import { IconNames } from '@blueprintjs/icons';
import { MultiSelect2 } from '@blueprintjs/select';
import * as React from 'react';

import { TEST_IDS, getResourceItemTestId } from 'src/constants/testIds';
import {
  projectResourceSelectItems,
  projectResourcesByIdSelector,
} from 'src/selectors/projectResource';
import { ProjectResource } from 'src/store/state/projects';
import * as copy from 'src/templates/copy';
import { useReduxSelector } from 'src/utils/hooks/useReduxSelector';
import { useRouterParams } from 'src/utils/hooks/useRouterParams';
import { toggleMultiSelectItemId } from 'src/utils/select/toggleMultiSelectItemId';

export type ResourceSelectorProps = {
  disabled?: boolean;
  popoverPosition?: PopoverPosition;
  selected: string[];
  setSelected: React.Dispatch<React.SetStateAction<string[]>>;
};

const resourcePredicate = (search: string, resource: ProjectResource) =>
  resource.name.toLowerCase().includes(search.toLowerCase());

export const ResourceSelector: React.VFC<ResourceSelectorProps> = ({
  disabled = false,
  popoverPosition = Position.BOTTOM_LEFT,
  selected,
  setSelected,
}) => {
  const { projectId } = useRouterParams();

  const resourceItems = useReduxSelector(projectResourceSelectItems(projectId));
  const resourcesById = useReduxSelector(
    projectResourcesByIdSelector(projectId)
  );

  const selectedItems = React.useMemo(
    () =>
      (selected || [])
        .filter((id) => resourcesById[id] !== undefined)
        .map((id) => resourcesById[id]),
    [resourcesById, selected]
  );

  return (
    <div data-testid={TEST_IDS.resource.selector}>
      <MultiSelect2<ProjectResource>
        disabled={disabled}
        fill
        itemPredicate={resourcePredicate}
        itemRenderer={(
          { id, name },
          { handleClick, modifiers: { active, disabled, matchesPredicate } }
        ) => {
          if (!matchesPredicate) {
            return null;
          }
          return (
            <MenuItem
              key={id}
              active={active}
              data-testid={getResourceItemTestId(id)}
              disabled={disabled}
              icon={selected.includes(id) ? IconNames.TICK : IconNames.BLANK}
              onClick={handleClick}
              shouldDismissPopover={false}
              text={name}
            />
          );
        }}
        items={resourceItems}
        onItemSelect={(selectedResource) =>
          setSelected(toggleMultiSelectItemId(selectedResource.id))
        }
        onRemove={(removedResource) =>
          setSelected((prevSelected) =>
            prevSelected.filter((id) => id !== removedResource.id)
          )
        }
        placeholder={copy.components.resourceSelector.placeholder}
        popoverProps={{
          rootBoundary: 'document',
          minimal: true,
          popoverClassName: 'sd-popover-menu',
          position: popoverPosition,
        }}
        resetOnSelect
        scrollToActiveItem={false}
        selectedItems={selectedItems}
        tagRenderer={(resource) => resource.name}
      />
    </div>
  );
};
